import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useRedditConnection } from '@/hooks/useRedditConnection'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  AlertTriangle, 
  RefreshCw, 
  Wifi
} from 'lucide-react'

interface RedditCredentialStatusProps {
  className?: string
}

export default function RedditCredentialStatus({ 
  className = ""
}: RedditCredentialStatusProps) {
  const { 
    isConnected, 
    isTokenExpired, 
    isTokenExpiringSoon, 
    needsReauth, 
    initiateReauth
  } = useRedditConnection()
  
  const { toast } = useToast()
  const [isReconnecting, setIsReconnecting] = useState(false)

  // Auto-prompt for re-authentication when tokens expire
  useEffect(() => {
    if (isTokenExpired && isConnected) {
      toast({
        title: "Reddit Connection Expired",
        description: "Please reconnect your Reddit account to continue posting",
        variant: "destructive"
      })
    }
  }, [isTokenExpired, isConnected, toast])

  const handleReconnect = async () => {
    setIsReconnecting(true)
    try {
      await initiateReauth()
    } catch (error) {
      console.error('Error initiating reauth:', error)
      toast({
        title: "Connection Error",
        description: "Please try connecting your Reddit account again",
        variant: "destructive"
      })
      setIsReconnecting(false)
    }
  }

  // Only show when re-authentication is needed
  if (!isConnected || (!isTokenExpired && !needsReauth && !isTokenExpiringSoon)) {
    return null
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className={className}
      >
        <Card className="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-blue-600 dark:text-blue-400">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                
                <div>
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    Reddit connection needs updating
                  </span>
                  <div className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Reconnect to continue posting to Reddit
                  </div>
                </div>
              </div>

              <Button
                onClick={handleReconnect}
                disabled={isReconnecting}
                size="sm"
                className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isReconnecting ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Wifi className="h-4 w-4" />
                    Reconnect
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  )
} 